<template>
  <div class="sepa">
    <transition
      v-if="fetchState.status.value === 'success'"
      appear
      enter-active-class="animate__animated animate__fadeIn a3"
    >
      <ui-well
        class="sepa__form"
      >
        <div class="d-flex justify-content-space-between px-6">
          <img class="h-3" :src="'/images/payment/logo-sepa.png'" alt="Sepa logo">
          <p class="font-weight-light">Paiement sécurisé</p>
        </div>
        <p class="text-center text-uppercase my-3 font-weight-bold font-size-130">
          Montant à payer: {{ toString_(price) }}&nbsp;&euro;
        </p>
        <div class="d-flex flex-direction-column">
          <div class="form-group">
            <div class="form-group__label">
              <ui-label for="lastName">Nom</ui-label>
            </div>
            <div class="form-group__field">
              <ui-form-input
                v-model="nom"
                placeholder="Nom"
                name="lastName"
              />
            </div>
          </div>
          <div class="form-group">
            <div class="form-group__label">
              <ui-label for="firstName">Prénom</ui-label>
            </div>
            <div class="form-group__field">
              <ui-form-input
                v-model="prenom"
                placeholder="Nom"
                name="firstName"
              />
            </div>
          </div>
          <div class="form-group">
            <div class="form-group__label">
              <ui-label for="titulaire_compte">Titulaire du compte</ui-label>
            </div>
            <div class="form-group__field">
              <ui-form-input
                v-model="titulaire_compte"
                placeholder="Titulaire du compte"
                name="titulaire_compte"
              />
            </div>
          </div>
          <div class="form-group">
            <div class="form-group__label">
              <ui-label for="iban">IBAN</ui-label>
            </div>
            <div class="form-group__field">
              <ui-form-input
                v-model="iban"
                placeholder="IBAN"
                name="iban"
              />
            </div>
          </div>
        </div>
        <p class="text-center font-size-70 font-weight-light text-italic color-gray-medium mt-1 mb-3">
          Vous devez être le titulaire de ce compte, ou disposer des autorisations nécessaires pour agir en son nom.
          Ces coordonnées ne sont pas enregistrées par GHS et ne pourront donc pas être réutilisées pour un prochain paiement.
        </p>
        <layout-cta-super
          component="ui-button"
          class="sepa__button"
          :variant="UI.VARIANT.PRIMARY"
          :status="paymentButtonStatus"
          @click="createPayment"
        >
          Passer à l'étape de réglement
        </layout-cta-super>

        <div class="d-flex justify-content-center mt-1">
          <ui-link
            class="sepa__back"
            :route="goBackRoute()"
          >
            Annuler l'achat
          </ui-link>
        </div>
      </ui-well>
    </transition>
  </div>
</template>

<style lang="sass" scoped>
@import ~/assets/sass/modules/layout/members
@import ~/assets/sass/modules/client/members

.sepa
  height: 90vh // approximate page height accounting for header and footer
  width: 100vw
  background-image: image('/patterns/so-white.png')
  background-repeat: repeat
  position: relative

  .form-group
    margin-bottom: space-x(2)

    &__label
      .ui-label
        font-weight: $font-weight-light

  :deep(.ui-form-checkbox)

    .custom-control-label
      font-size: 80%
      font-weight: $font-weight-light

  &__form
    max-width: 500px
    padding: space-x(6)
    position: absolute
    top: 50%
    right: 50%
    transform: translate(50%,-50%)
    box-shadow: $shadow-discreet
    color: $black

  &__button
    margin-top: auto
    width: 100%
    margin-bottom: space-x(1)
    margin-top: space-x(2)

  &__back
    font-size: 80%
    font-weight: $font-weight-bold
    text-transform: uppercase

    &:hover
      text-decoration: underline
      text-underline-offset: 2px
      text-decoration-color: $primary
</style>

<script lang="ts">
import { mapState } from 'pinia'
import { useLegacyPageFetch } from '~/composables/core/hook'
import { useLinkHandler } from '~/composables/core/link-handler'
import { useDataOnlyNavigationContext } from '~/composables/layout/navigation'
import { useToastBuilder } from '~/composables/layout/toast'
import Console from '~/utils/lib/core/console'
import { any, isEmpty, toString, replace } from '~/utils/lib/core/prelude'
import { useStore_hamac_signatures_paiements_sepa } from '~/stores/hamac/signatures/paiements/sepa'
import { toString as toString_ } from '~/utils/lib/core/float'
import { getSignaturesRoute, getSignaturesSepaPayRoute } from '~/stores/parametres'
import { NAVIGATION_SCOPE } from '~/utils/lib/layout/types'

const console = new Console('component:page-parametres-sepa-form')

export default defineNuxtComponent({
  setup() {
    const navigationContext = useDataOnlyNavigationContext()

    definePageMeta({
      navigationScope: NAVIGATION_SCOPE.DATA_ONLY,
      layout: 'blank',
      middleware: [
        'session-control-gate',
        'sepa-payment-gate'
      ],
    })
    useHead({
      title: `Paiement SEPA — ${navigationContext.fromStructureLevel().structure.raison}`,
    })

    const {
      fetchState,
      fetchWrapper,
    } = useLegacyPageFetch()

    const ToastBuilder = useToastBuilder()

    const store_signature_paiements_sepa = useStore_hamac_signatures_paiements_sepa()

    return {
      ...navigationContext,
      ...useLinkHandler(),
      fetchState,
      fetchWrapper,
      ToastBuilder,
      store_signature_paiements_sepa,
    }
  },
  data() {
    return {
      nom: '', // lazy
      prenom: '', // lazy
      titulaire_compte: '', // lazy
      iban: '', // lazy
      isPaying: false,
    }
  },
  computed: {
    ...mapState(useStore_hamac_signatures_paiements_sepa, [
      'price',
      'code',
      'id',
    ]),
    paymentButtonStatus() {
      if (any(isEmpty)([this.nom, this.prenom, this.titulaire_compte, this.iban])) return this.UI.STATUS.DISABLED
      if (this.isPaying) return this.UI.STATUS.DEFFERED
      return this.UI.STATUS.ENABLED
    }
  },
  created() {
    this.fetchWrapper(
      () => this.fetch()
    )
  },
  methods: {
    toString_,
    goBackRoute() {
      const {
        structure: { id },
      } = this.fromStructureLevel()

      return getSignaturesRoute(id)
    },
    async createPayment() {
      try {
        if (!this.isPaying) {
          this.isPaying = true

          this.store_signature_paiements_sepa.$patch(
            {
              titulaire_compte: this.titulaire_compte,
              nom: this.nom,
              prenom: this.prenom,
              iban: replace(/\s/g, '', this.iban),
            }
          )

          const sepa = this.store_signature_paiements_sepa.$state

          const {
            structure: { id, code_client },
          } = this.fromStructureLevel()

          await this.store_signature_paiements_sepa.createPayment(
            {
              code: code_client,
              prenom: sepa.prenom,
              nom: sepa.nom,
              email: sepa.email,
              iban: sepa.iban,
              titulaire_compte: sepa.titulaire_compte,
              reference: sepa.reference,
              article: (sepa.pack as unknown as { id: string }).id,
              remise_sma: sepa.remise_sma
            }
          )

          const route = getSignaturesSepaPayRoute(id)

          this.goToRoute(route)
        }
      } catch (err) {
        console.warn(err, 'sepa payment creation error')
        this.isPaying = false

        return this.ToastBuilder
          .error('Erreur lors du paiement: ' + toString(err))
          .title('Signatures')
          .show()
      }
    },
    fetch() {
      const sepaStore = this.store_signature_paiements_sepa.$state

      this.nom = sepaStore.nom
      this.prenom = sepaStore.prenom
      this.titulaire_compte = sepaStore.titulaire_compte
      this.iban = sepaStore.iban

      return Promise.resolve()
    }
  }
})
</script>
